/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import * as zod from 'zod';

/**
 * @summary Agents approved in the workspace, newest first. Owners see everyone's; others their own.
 */
export const ListAgentConnectionsParams = zod.object({
  id: zod.string(),
});

export const ListAgentConnectionsResponseItem = zod.object({
  role: zod.enum(['OWNER', 'EDITOR', 'REVIEWER']),
  id: zod.string(),
  agentName: zod.string().describe('What the agent registered as.'),
  approvedBy: zod
    .object({
      id: zod.string(),
      name: zod.string().nullable(),
      email: zod.string().nullable(),
    })
    .describe('Who approved it; the agent acts as them.'),
  approvedAt: zod.iso.datetime({ offset: true }),
  lastUsedAt: zod.iso.datetime({ offset: true }).nullable(),
  isYours: zod.boolean().describe('Approved by the signed-in user.'),
});
export const ListAgentConnectionsResponse = zod.array(ListAgentConnectionsResponseItem);

/**
 * @summary Change an agent's role. Whoever approved it, or an owner.
 */
export const UpdateAgentConnectionParams = zod.object({
  id: zod.string(),
  connectionId: zod.string(),
});

export const UpdateAgentConnectionBody = zod.object({
  role: zod.enum(['OWNER', 'EDITOR', 'REVIEWER']),
});

export const UpdateAgentConnectionResponse = zod.void();

/**
 * @summary Revoke an agent; its next request is refused. Whoever approved it, or an owner.
 */
export const RevokeAgentConnectionParams = zod.object({
  id: zod.string(),
  connectionId: zod.string(),
});

export const RevokeAgentConnectionResponse = zod.void();

/**
 * @summary API keys that still work, newest first. Owners see everyone's; others their own.
 */
export const ListApiKeysParams = zod.object({
  id: zod.string(),
});

export const ListApiKeysResponseItem = zod.object({
  role: zod.enum(['OWNER', 'EDITOR', 'REVIEWER']),
  id: zod.string(),
  name: zod.string(),
  preview: zod.string().describe("The key's start and end, to tell keys apart."),
  createdBy: zod
    .object({
      id: zod.string(),
      name: zod.string().nullable(),
      email: zod.string().nullable(),
    })
    .describe('Who made it; the key acts as them.'),
  createdAt: zod.iso.datetime({ offset: true }),
  lastUsedAt: zod.iso.datetime({ offset: true }).nullable(),
  isYours: zod.boolean().describe('Created by the signed-in user.'),
});
export const ListApiKeysResponse = zod.array(ListApiKeysResponseItem);

/**
 * @summary Create an API key. The response is the only time the key is shown.
 */
export const CreateApiKeyParams = zod.object({
  id: zod.string(),
});

export const createApiKeyBodyNameMax = 80;

export const CreateApiKeyBody = zod.object({
  role: zod.enum(['OWNER', 'EDITOR', 'REVIEWER']),
  name: zod.string().min(1).max(createApiKeyBodyNameMax).describe("What it's for."),
});

export const CreateApiKeyResponse = zod.object({
  role: zod.enum(['OWNER', 'EDITOR', 'REVIEWER']),
  id: zod.string(),
  name: zod.string(),
  preview: zod.string().describe("The key's start and end, to tell keys apart."),
  createdBy: zod
    .object({
      id: zod.string(),
      name: zod.string().nullable(),
      email: zod.string().nullable(),
    })
    .describe('Who made it; the key acts as them.'),
  createdAt: zod.iso.datetime({ offset: true }),
  lastUsedAt: zod.iso.datetime({ offset: true }).nullable(),
  isYours: zod.boolean().describe('Created by the signed-in user.'),
  key: zod.string(),
});

/**
 * @summary Revoke an API key; requests with it are refused from now on. Its creator, or an owner.
 */
export const RevokeApiKeyParams = zod.object({
  id: zod.string(),
  keyId: zod.string(),
});

export const RevokeApiKeyResponse = zod.void();

/**
 * @summary The signed-in user's agent connection, and the workspaces they could approve it for.
 */
export const GetAgentConnectionRequestParams = zod.object({
  connectionId: zod.string(),
});

export const GetAgentConnectionRequestResponse = zod.object({
  status: zod.enum(['PENDING', 'ACTIVE', 'REVOKED']),
  role: zod.union([zod.enum(['OWNER', 'EDITOR', 'REVIEWER']), zod.null()]),
  id: zod.string(),
  agentName: zod.string(),
  workspaceId: zod.string().nullable().describe('Set once approved.'),
  workspaceName: zod.string().nullable(),
  createdAt: zod.iso.datetime({ offset: true }),
  workspaces: zod
    .array(
      zod.object({
        role: zod
          .enum(['OWNER', 'EDITOR', 'REVIEWER'])
          .describe("The user's own role there."),
        grantableRoles: zod
          .array(zod.enum(['OWNER', 'EDITOR', 'REVIEWER']))
          .describe(
            "Roles they can give an agent: their own, or any if they're an owner.",
          ),
        id: zod.string(),
        name: zod.string(),
      }),
    )
    .describe('Where the agent could work.'),
});

/**
 * @summary Let the agent work in a workspace, with a role.
 */
export const ApproveAgentConnectionParams = zod.object({
  connectionId: zod.string(),
});

export const ApproveAgentConnectionBody = zod.object({
  role: zod.enum(['OWNER', 'EDITOR', 'REVIEWER']),
  workspaceId: zod.uuid(),
});

export const ApproveAgentConnectionResponse = zod.object({
  status: zod.enum(['PENDING', 'ACTIVE', 'REVOKED']),
  role: zod.union([zod.enum(['OWNER', 'EDITOR', 'REVIEWER']), zod.null()]),
  id: zod.string(),
  agentName: zod.string(),
  workspaceId: zod.string().nullable().describe('Set once approved.'),
  workspaceName: zod.string().nullable(),
  createdAt: zod.iso.datetime({ offset: true }),
  workspaces: zod
    .array(
      zod.object({
        role: zod
          .enum(['OWNER', 'EDITOR', 'REVIEWER'])
          .describe("The user's own role there."),
        grantableRoles: zod
          .array(zod.enum(['OWNER', 'EDITOR', 'REVIEWER']))
          .describe(
            "Roles they can give an agent: their own, or any if they're an owner.",
          ),
        id: zod.string(),
        name: zod.string(),
      }),
    )
    .describe('Where the agent could work.'),
});

/**
 * @summary Turn the agent away; it can sign in again later to ask again.
 */
export const DeclineAgentConnectionParams = zod.object({
  connectionId: zod.string(),
});

export const DeclineAgentConnectionResponse = zod.void();
